import MusicNotationOffIcon from '@/assets/images/icons/music-notation-off-icon.svg'
import MusicIcon from '@/assets/images/icons/music-icon.svg'
import React, { useState } from 'react'
import { Button, XStack, useMedia } from 'tamagui'

export type NotationMode = 'video-only' | 'video-notation'

type Props = {
  notationMode: NotationMode
  hasNotation: boolean | null
  onNotationChange: (m: NotationMode) => void
  disabled?: boolean
  toolbarCompact?: boolean
  reserveSpace?: boolean
}

export default function VideoActionButtons({
  notationMode,
  onNotationChange,
  disabled,
  hasNotation,
  toolbarCompact = false,
  reserveSpace = false,
}: Props) {
  const media = useMedia()
  const [isHovered, setIsHovered] = useState(false)

  const iconSize = 20
  const notationOn = notationMode === 'video-notation'
  const isDisabled = disabled || hasNotation !== true

  const iconColor = isDisabled
    ? '#C9C5C3'
    : toolbarCompact && isHovered
      ? '#FFFFFF'
      : '#0D0D0D'

  const notationIcon = notationOn ? (
    <MusicIcon width={iconSize} height={iconSize} color={iconColor} />
  ) : (
    <MusicNotationOffIcon width={iconSize} height={iconSize} color={iconColor} />
  )

  const notationBoxShadow =
    '0px 2px 4px 0px rgba(0,0,0,0.12), 0px 0px 2px 0px rgba(0,0,0,0.08)'

  const compactCircleStyle = {
    width: 40,
    height: 40,
    borderRadius: 30,
    padding: 10,
    paddingHorizontal: 0,
    paddingVertical: 0,
    minWidth: 40,
    maxWidth: 40,
    boxShadow: notationBoxShadow,
    cursor: isDisabled ? ('not-allowed' as const) : ('pointer' as const),
    opacity: isDisabled ? 0.4 : 1,
  }

  if (hasNotation === null && !reserveSpace && !toolbarCompact) return null

  const handleToggle = () => {
    if (isDisabled) return
    onNotationChange(notationOn ? 'video-only' : 'video-notation')
  }

  const label = notationOn ? 'Notation on' : 'Notation off'
  const accessibilityLabel = isDisabled
    ? 'Notation unavailable'
    : notationOn
      ? 'Turn notation off'
      : 'Turn notation on'

  return (
    <XStack ai="center" jc="flex-end" gap="$2" $gtSm={{ gap: '$3' }}>
      <Button
        bg={toolbarCompact ? '#FFFFFF' : notationOn ? '#E7D9C9' : '#FFFFFF'}
        color={toolbarCompact ? '#0D0D0D' : notationOn ? '#6B4E2E' : '#1C1C1C'}
        br={toolbarCompact ? 30 : 8}
        borderWidth={toolbarCompact ? 1 : undefined}
        borderColor={toolbarCompact ? '#C9C5C3' : undefined}
        px={toolbarCompact ? 0 : media.gtSm ? '$3' : '$2'}
        py="$2"
        minWidth={toolbarCompact ? 40 : media.gtSm ? 180 : undefined}
        maxWidth={toolbarCompact ? 40 : media.gtSm ? 220 : undefined}
        icon={notationIcon}
        hoverStyle={
          toolbarCompact && !isDisabled
            ? { backgroundColor: '#0D0D0D', borderWidth: 1, borderColor: '#0D0D0D' }
            : { opacity: 0.9 }
        }
        pressStyle={
          toolbarCompact && !isDisabled
            ? { backgroundColor: '#0D0D0D', borderWidth: 1, borderColor: '#0D0D0D' }
            : isDisabled
              ? {}
              : { opacity: 0.9 }
        }
        onHoverIn={() => !isDisabled && setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={
          toolbarCompact
            ? compactCircleStyle
            : {
                boxShadow: notationBoxShadow,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.4 : 1,
              }
        }
        disabled={isDisabled}
        size="$3"
        fontWeight="600"
        onPress={handleToggle}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="switch"
        accessibilityState={{ checked: notationOn, disabled: isDisabled }}
      >
        {toolbarCompact ? null : media.gtSm ? label : null}
      </Button>
    </XStack>
  )
}
